import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  fetchWorkLogs,
  createWorkLog,
  updateWorkLog,
  deleteWorkLog,
  transitionWorkLog,
} from "../services/payrollService";
import {
  WORK_LOG_TYPE_OPTIONS,
  WORK_LOG_ORIGIN_OPTIONS,
  workLogTypeLabel,
  workLogStatusLabel,
  WORK_LOG_STATUS_TONES,
  workLogOriginLabel,
  buildThemeClasses,
  formatAmount,
} from "../theme";
import type { WorkLogStatus, WorkLog, WorkLogType } from "../types";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";
import HoursReviewPanel from "../components/HoursReviewPanel";

// ---------------------------------------------------------------------------
// Autorización de incidencias del equipo para nómina (AgroCloud#81).
//
// El tablero del supervisor: lo que se capturó por el equipo (asistencia, entregable,
// bonos, descuentos), posiblemente desde la app móvil y en varios sitios
// (oficina, remoto, sitio del cliente), espera aquí su autorización.
//
// EL CRITERIO DE LA HISTORIA — "solo lo autorizado llega a la nómina" — NO se
// defiende en esta pantalla: se defiende en el backend y en `@torre/payroll`
// (`filterPayableWorkLogs` + la máquina de estados). Esta sección es la
// cara visible de esa regla, no su guardián. Aunque alguien llame la API a mano,
// una incidencia `submitted` no puede saltar a `sent_to_payroll`.
//
// Toda incidencia nace `submitted`, aunque el cliente pida otra cosa: nadie se
// autoriza a sí mismo. Rechazar exige un motivo — el colaborador merece saber
// qué corregir.
//
// #1346: «Horas por aprobar» dejó de vivir aquí arriba; es una sección aparte
// (`/payroll/hours-approval`). En su lugar va la tabla por colaborador
// (`HoursReviewPanel`): registrado, justificadas, por aprobar, no justificadas,
// pérdida o ganancia contra lo esperado y tiempo por repo. Las incidencias de
// abajo siguen igual.
// ---------------------------------------------------------------------------

/** Número del formulario, o `null` si no se informó (≠ 0). */
function numOrNull(raw: string | undefined): number | null {
  if (raw === undefined || raw === null || String(raw).trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function toPayload(values: Record<string, string>): Partial<WorkLog> {
  return {
    date: values.date,
    teamMemberId: values.teamMemberId,
    teamMemberName: values.teamMemberName || null,
    squadId: values.squadId || null,
    squadName: values.squadName || null,
    locationId: values.locationId || null,
    locationName: values.locationName || null,
    type: (values.type || "attendance") as WorkLogType,
    concept: values.concept || null,
    amount: numOrNull(values.amount),
    // Entregable: el pago lo calcula el backend delegando en la tarifa del módulo
    // Colaboradores (AgroCloud#13). Aquí solo se declara CON QUÉ calcularlo.
    payRateId: values.payRateId || null,
    payRateName: values.payRateName || null,
    output: numOrNull(values.output),
    days: numOrNull(values.days),
    hours: numOrNull(values.hours),
    // Datos de las tarifas por proyecto y de sueldo fijo (AgroCloud#13): el
    // backend ya los persiste y los usa para calcular el pago. `null` (no 0)
    // cuando no se informan: cero avance o cero periodos NO es un pago de 0.
    progressPercentage: numOrNull(values.progressPercentage),
    previouslyPaid: numOrNull(values.previouslyPaid),
    periods: numOrNull(values.periods),
    origin: (values.origin || "web") as "web" | "app",
    notes: values.notes || null,
  };
}

export default function WorkLogsSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  const isStaffAdmin = useIsStaffAdmin("payroll");
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [rows, setRows] = useState<WorkLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await fetchWorkLogs();
      setRows(items);
    } catch {
      setError(t("payroll.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Conteo del tablero: cuánto espera autorización (AgroCloud#81). */
  const summary = useMemo(() => {
    const acc: Record<string, number> = {
      submitted: 0,
      approved: 0,
      rejected: 0,
      sent_to_payroll: 0,
    };
    for (const i of rows) {
      const status = String(i.status || "submitted");
      if (status in acc) acc[status] += 1;
    }
    return acc;
  }, [rows]);

  const handleTransition = async (
    id: string,
    status: WorkLogStatus,
    rejectionReason?: string,
  ) => {
    setActionError(null);
    try {
      await transitionWorkLog(id, status, rejectionReason ? { rejectionReason } : {});
      await load();
    } catch {
      setActionError(t("payroll.transitionError"));
    }
  };

  const handleReject = async (id: string) => {
    // Rechazar sin motivo deja al colaborador sin saber qué corregir; el backend
    // también lo rechaza, esto solo evita el viaje.
    const reason = window.prompt(t("payroll.rejectionReasonPrompt") ?? "");
    if (!reason || !reason.trim()) return;
    await handleTransition(id, "rejected", reason.trim());
  };

  const badge = (tone: string, label: string) => (
    <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${tone}`}>{label}</span>
  );

  const columns: Column[] = [
    { key: "date", label: t("payroll.fields.date"), width: "104px" },
    {
      key: "teamMemberName",
      label: t("payroll.fields.teamMember"),
      render: (row) => String(row.teamMemberName || row.teamMemberId || "—"),
    },
    {
      key: "locationName",
      label: t("payroll.fields.location"),
      render: (row) => String(row.locationName || row.locationId || "—"),
    },
    {
      key: "type",
      label: t("payroll.fields.workLogType"),
      render: (row) => {
        const type = String(row.type || "attendance");
        return workLogTypeLabel(t, type);
      },
    },
    {
      key: "concept",
      label: t("payroll.fields.concept"),
      render: (row) => String(row.concept || row.payRateName || "—"),
    },
    {
      key: "amount",
      label: t("payroll.fields.amount"),
      align: "right",
      // "—" cuando el monto lo resolverá la tarifa: no es un bono de 0.
      render: (row) => formatAmount(row.amount as number | null, String(row.currency || "MXN")),
    },
    {
      key: "status",
      label: t("payroll.fields.workLogStatus"),
      render: (row) => {
        const status = String(row.status || "submitted");
        return badge(
          WORK_LOG_STATUS_TONES[status] ?? "bg-gray-400/15 text-gray-500",
          workLogStatusLabel(t, status),
        );
      },
    },
    {
      key: "origin",
      label: t("payroll.fields.origin"),
      render: (row) => {
        const origin = String(row.origin || "web");
        return workLogOriginLabel(t, origin);
      },
    },
  ];

  const fields: FormField[] = [
    { key: "date", label: t("payroll.fields.date"), type: "date", required: true },
    { key: "teamMemberId", label: t("payroll.fields.teamMemberId"), required: true },
    { key: "teamMemberName", label: t("payroll.fields.teamMemberName") },
    { key: "squadId", label: t("payroll.fields.squadId") },
    { key: "squadName", label: t("payroll.fields.squadName") },
    { key: "locationId", label: t("payroll.fields.locationId"), hint: t("payroll.hints.location") },
    { key: "locationName", label: t("payroll.fields.locationName") },
    {
      key: "type",
      label: t("payroll.fields.workLogType"),
      type: "select",
      defaultValue: "attendance",
      options: WORK_LOG_TYPE_OPTIONS.map((v) => ({
        value: v,
        label: workLogTypeLabel(t, v),
      })),
    },
    { key: "concept", label: t("payroll.fields.concept") },
    {
      key: "amount",
      label: t("payroll.fields.amount"),
      type: "number",
      hint: t("payroll.hints.workLogAmount"),
    },
    {
      key: "payRateId",
      label: t("payroll.fields.payRateId"),
      hint: t("payroll.hints.payRate"),
    },
    { key: "payRateName", label: t("payroll.fields.payRateName") },
    { key: "output", label: t("payroll.fields.output"), type: "number" },
    { key: "days", label: t("payroll.fields.days"), type: "number" },
    { key: "hours", label: t("payroll.fields.hours"), type: "number" },
    // Esta pantalla NO conoce el catálogo de tarifas (la tarifa se teclea por
    // id), así que no puede ocultar por modalidad: cada campo dice en su
    // etiqueta y en su ayuda a qué modalidad pertenece.
    {
      key: "progressPercentage",
      label: t("payroll.fields.progressPercentageProject"),
      type: "number",
      hint: t("payroll.hints.progressPercentage"),
    },
    {
      key: "previouslyPaid",
      label: t("payroll.fields.previouslyPaidProject"),
      type: "number",
      hint: t("payroll.hints.previouslyPaid"),
    },
    {
      key: "periods",
      label: t("payroll.fields.periodsFixedSalary"),
      type: "number",
      hint: t("payroll.hints.workLogPeriods"),
    },
    {
      key: "origin",
      label: t("payroll.fields.origin"),
      type: "select",
      defaultValue: "web",
      options: WORK_LOG_ORIGIN_OPTIONS.map((v) => ({
        value: v,
        label: workLogOriginLabel(t, v),
      })),
    },
    { key: "notes", label: t("payroll.fields.notes"), type: "textarea" },
  ];

  /** Botones de autorización por fila — el acto central de la historia (AgroCloud#81). */
  const extraActions = (row: Row) => {
    if (!isStaffAdmin) return null;
    const status = String(row.status || "submitted");
    return (
      <>
        {status === "submitted" && (
          <>
            <button
              title={t("payroll.approve")}
              aria-label={`approve-${row.id}`}
              onClick={() => handleTransition(row.id, "approved")}
              className="rounded-md p-1.5 text-emerald-500 hover:bg-emerald-500/10"
            >
              <i className="ri-check-line" />
            </button>
            <button
              title={t("payroll.reject")}
              aria-label={`reject-${row.id}`}
              onClick={() => handleReject(row.id)}
              className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
            >
              <i className="ri-close-line" />
            </button>
          </>
        )}
        {status === "approved" && (
          <button
            title={t("payroll.sendToPayroll")}
            aria-label={`send-${row.id}`}
            onClick={() => handleTransition(row.id, "sent_to_payroll")}
            className="rounded-md p-1.5 text-sky-500 hover:bg-sky-500/10"
          >
            <i className="ri-send-plane-line" />
          </button>
        )}
      </>
    );
  };

  /** Tablero de conteos + el aviso de lo que espera autorización (AgroCloud#81). */
  const banner = (
    <div className="mb-4 space-y-3">
      {/* #1346: horas por colaborador del periodo, antes de las incidencias. */}
      <HoursReviewPanel />
      <h2 className={`flex items-center gap-2 pt-1 text-base font-semibold ${tc.text}`} data-testid="work-logs-heading">
        <i className="ri-shield-check-line text-primary-500" />
        {t("payroll.hoursReview.workLogsHeading")}
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(["submitted", "approved", "sent_to_payroll", "rejected"] as const).map((status) => (
          <div
            key={status}
            className={`rounded-xl border p-3 ${tc.cardBorder} ${tc.cardBg}`}
          >
            <p className={`text-xs ${tc.textSecondary}`}>
              {workLogStatusLabel(t, status)}
            </p>
            <p className={`text-xl font-bold ${tc.text}`}>{summary[status]}</p>
          </div>
        ))}
      </div>

      {summary.submitted > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-500">
          <i className="ri-time-line mr-1" />
          {t("payroll.pendingWorkLogs", { count: summary.submitted })}
        </div>
      )}

      {actionError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500">
          <i className="ri-error-warning-line mr-1" />
          {actionError}
        </div>
      )}
    </div>
  );

  const handleCreate = async (values: Record<string, string>) => {
    await createWorkLog(toPayload(values));
    await load();
  };

  const handleUpdate = async (id: string, values: Record<string, string>) => {
    await updateWorkLog(id, toPayload(values));
    await load();
  };

  const handleDelete = async (id: string) => {
    await deleteWorkLog(id);
    await load();
  };

  return (
    <>
      <ApiCrudSection
        title={t("payroll.menu.workLogs")}
        subtitle={t("payroll.workLogsSubtitle")}
        icon="ri-shield-check-line"
        addLabel={t("payroll.newWorkLog")}
        columns={columns}
        fields={fields}
        rows={rows as unknown as Row[]}
        loading={loading}
        error={error}
        searchKey="teamMemberName"
        banner={banner}
        extraActions={extraActions}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </>
  );
}
